import React from "react";
import Plot from "react-plotly.js";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { FileExcel } from 'react-bootstrap-icons';
import ExcelJS from "exceljs";

const PieChart3DCustom = ({ data, namekey, datakey, title, observaciones }) => {

  // Preparar datos para Plotly 3D Pie
  const chartData = data.map((item) => ({
    name: item[namekey],
    value: item[datakey],
  }));

  const labels = chartData.map((item) => item.name);
  const values = chartData.map((item) => item.value);

  const plotData = [
    {
      type: 'pie',
      labels: labels,
      values: values,
      hole: 0.0, // 0 para pie normal, >0 para donut
      pull: 0.05, // Separación de las porciones para efecto 3D
      marker: {
        colors: [
          '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
          '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
        ],
        line: {
          color: '#ffffff',
          width: 2
        }
      },
      textinfo: 'label+percent',
      textposition: 'auto',
      hovertemplate: '<b>%{label}</b><br>' +
                     'Valor: %{value:,.0f}<br>' +
                     'Porcentaje: %{percent}<br>' +
                     '<extra></extra>',
      direction: 'clockwise',
      sort: false,
      // Propiedades para simular 3D
      domain: { x: [0.1, 0.9], y: [0.1, 0.9] },
    }
  ];

  const layout = {
    title: {
      text: title || 'Gráfico Circular 3D',
      font: { size: 18, color: '#344767' }
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    showlegend: true,
    legend: {
      orientation: 'v',
      x: 1.02,
      y: 0.5,
      xanchor: 'left',
      yanchor: 'middle',
      font: { size: 12 }
    },
    margin: { l: 20, r: 150, t: 80, b: 20 },
    height: 500,
    annotations: [
      {
        text: '',
        showarrow: false,
        font: { size: 20 }
      }
    ]
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d', 'zoom2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d'],
    toImageButtonOptions: {
      format: 'png',
      filename: title || 'grafico_pie_3d',
      height: 800,
      width: 1200,
      scale: 2
    }
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    // Calcular totales y porcentajes
    const total = values.reduce((acc, val) => acc + val, 0);

    // Encabezados
    const headers = ['Categoría', 'Valor', 'Porcentaje'];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    // Datos
    chartData.forEach((item) => {
      const percentage = ((item.value / total) * 100).toFixed(2) + '%';
      worksheet.addRow([item.name, item.value, percentage]);
    });

    // Fila de total
    const totalRow = worksheet.addRow(['TOTAL', total, '100%']);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
    });

    // Ajustar ancho de columnas
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'grafico'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox 
        sx={{ 
          backgroundColor: 'white', 
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </MDBox>

      {observaciones && (
        <MDTypography variant="caption" color="text" mt={2} style={{ fontStyle: 'italic', opacity: 0.7 }}>
          *{observaciones}
        </MDTypography>
      )}
    </MDBox>
  );
};

export default PieChart3DCustom;
